/**
 * `/admin/mail`: the Zoho Mail mailbox that sends password reset emails (spec 20).
 *
 * Nothing here carries a Zoho token. The server keeps the refresh token
 * encrypted and never returns it, so the panel only ever sees the status below
 * and the one-time `code` Zoho puts in the callback URL.
 */

import { request } from "./client";

export type MailStatus = {
  /** False when the server has no Zoho client configured. Only ops can fix that. */
  configured: boolean;
  /** False when FITZONE_TOKEN_ENCRYPTION_KEYS is missing or invalid; connecting is impossible until it is set. */
  encryption_configured: boolean;
  connected: boolean;
  provider: string | null;
  from_address: string | null;
  connected_at: string | null;
  connected_by_name: string | null;
  last_sent_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  /** Zoho rejected the stored authorisation, so sending has stopped until someone reconnects. */
  needs_reconnect: boolean;
};

export type MailAction = {
  status: MailStatus;
  message: string;
};

export function fetchMailStatus(): Promise<MailStatus> {
  return request<MailStatus>("/admin/mail");
}

/** Step one of connecting. The caller sends the browser to the returned URL. */
export function startZohoConnect(): Promise<{ authorize_url: string }> {
  return request<{ authorize_url: string }>("/admin/mail/zoho/authorize", { method: "POST" });
}

/** Step two. The callback page hands Zoho's `code` and our `state` back to the API. */
export function completeZohoConnect(code: string, state: string): Promise<MailAction> {
  return request<MailAction>("/admin/mail/zoho/callback", {
    method: "POST",
    body: { code, state },
  });
}

export function sendTestEmail(): Promise<MailAction> {
  return request<MailAction>("/admin/mail/test", { method: "POST" });
}

export function disconnectMailbox(): Promise<MailAction> {
  return request<MailAction>("/admin/mail", { method: "DELETE" });
}
